import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, EnvelopeSimple, Code, SoccerBall } from '@phosphor-icons/react';

const founders = [
  {
    name: 'David Ejoor',
    role: 'Founder',
    roleJp: '創設者',
    email: 'davidejoor4@gmail.com',
    photo: 'https://customer-assets-agu9un31.emergentagent.net/job_blue-lock-football/artifacts/33e02b404a9e9cc4_7BE097A1-76A4-406A-8646-AFEE5A9C2FB7.jpeg',
    accent: '#00E5FF',
    bio: 'Software engineer and football devotee. David envisioned JWFL as a bridge between Japanese football culture and the world of fantasy gaming — turning a passion into a platform.',
    tags: ['Founder', 'Engineer', 'Football'],
  },
  {
    name: 'John Dairo',
    role: 'Co-Founder',
    roleJp: '共同創設者',
    email: 'highkeyjohn@gmail.com',
    photo: 'https://customer-assets-agu9un31.emergentagent.net/job_blue-lock-football/artifacts/4624a41c3b19c343_IMG_1282.jpeg',
    accent: '#FF006E',
    bio: 'Full-stack engineer with a deep love for Japanese culture and the beautiful game. John brought the technical backbone and visual identity that makes JWFL come alive.',
    tags: ['Co-Founder', 'Engineer', 'Japanese Culture'],
  },
];

export const CreditsPage = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-16 px-4"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

      {/* Ambient glow blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[10%] left-[5%] w-[420px] h-[420px] rounded-full opacity-[0.07]"
          style={{ background: '#00E5FF', filter: 'blur(120px)' }} />
        <div className="absolute bottom-[15%] right-[5%] w-[380px] h-[380px] rounded-full opacity-[0.06]"
          style={{ background: '#FF006E', filter: 'blur(120px)' }} />
      </div>

      <div className={`max-w-5xl mx-auto transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#7A86B5] hover:text-[#00E5FF] transition-colors mb-10 tracking-widest uppercase"
          data-testid="credits-back-btn">
          <ArrowLeft size={14} /> Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#00E5FF] mb-3 font-medium">
            JWFL · 日本ワールドフットボールリーグ
          </p>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tight text-white mb-4"
            style={{ textShadow: '0 0 40px rgba(0,229,255,0.25)' }}>
            CREDITS
          </h1>
          <p className="text-[#7A86B5] text-sm max-w-md mx-auto leading-relaxed">
            Two engineers. One shared vision. A love for Japanese football that turned into a game.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="h-px flex-1 max-w-[80px]" style={{ background: 'linear-gradient(to right, transparent, rgba(0,229,255,0.4))' }} />
            <span className="text-[#00E5FF] text-xs tracking-[0.3em]">制作者</span>
            <div className="h-px flex-1 max-w-[80px]" style={{ background: 'linear-gradient(to left, transparent, rgba(0,229,255,0.4))' }} />
          </div>
        </div>

        {/* Founder Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {founders.map((f, i) => (
            <div
              key={f.name}
              data-testid={`credits-card-${i}`}
              className="relative rounded-2xl overflow-hidden group"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid rgba(${f.accent === '#00E5FF' ? '0,229,255' : '255,0,110'},0.2)`,
                boxShadow: `0 0 0 0 ${f.accent}`,
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = `0 0 32px rgba(${f.accent === '#00E5FF' ? '0,229,255' : '255,0,110'},0.15)`;
                e.currentTarget.style.borderColor = `rgba(${f.accent === '#00E5FF' ? '0,229,255' : '255,0,110'},0.5)`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 0 0 0 transparent';
                e.currentTarget.style.borderColor = `rgba(${f.accent === '#00E5FF' ? '0,229,255' : '255,0,110'},0.2)`;
              }}
            >
              {/* Photo */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src={f.photo}
                  alt={f.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0"
                  style={{ background: `linear-gradient(to bottom, transparent 40%, #04060F 100%)` }} />

                {/* Role badge — top-right */}
                <div className="absolute top-4 right-4">
                  <span className="text-[9px] font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full"
                    style={{
                      background: `rgba(${f.accent === '#00E5FF' ? '0,229,255' : '255,0,110'},0.15)`,
                      border: `1px solid ${f.accent}`,
                      color: f.accent,
                    }}>
                    {f.role}
                  </span>
                </div>
              </div>

              {/* Card body */}
              <div className="px-6 pt-2 pb-6">
                <div className="flex items-end justify-between mb-1">
                  <div>
                    <h2 className="text-2xl font-black text-white tracking-tight">{f.name}</h2>
                    <p className="text-[10px] tracking-[0.25em] mt-0.5" style={{ color: f.accent }}>{f.roleJp}</p>
                  </div>
                </div>

                <p className="text-[#7A86B5] text-sm leading-relaxed mt-3 mb-4">{f.bio}</p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {f.tags.map(tag => (
                    <span key={tag} className="text-[9px] font-medium tracking-widest uppercase px-2 py-0.5 rounded-sm"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        color: '#7A86B5',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}>
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Email */}
                <a href={`mailto:${f.email}`}
                  className="inline-flex items-center gap-2 text-xs transition-colors"
                  style={{ color: f.accent }}
                  data-testid={`credits-email-${i}`}>
                  <EnvelopeSimple size={14} />
                  {f.email}
                </a>
              </div>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{ background: `linear-gradient(to right, transparent, ${f.accent}, transparent)` }} />
            </div>
          ))}
        </div>

        {/* Story section */}
        <div className="rounded-2xl p-8 mb-12 relative overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(0,229,255,0.1)',
          }}>
          <div className="absolute top-0 right-0 w-48 h-48 opacity-[0.04]"
            style={{ background: 'radial-gradient(circle, #00E5FF, transparent)', borderRadius: '50%', transform: 'translate(30%, -30%)' }} />

          <h3 className="text-sm font-bold tracking-[0.25em] uppercase text-[#00E5FF] mb-4">Our Story · ストーリー</h3>
          <p className="text-[#9AA3C8] text-sm leading-loose max-w-2xl">
            David and John are passionate software engineers with a shared love for sports and Japanese culture.
            United by their fascination with the J.League, manga football aesthetics, and the dream of building
            something uniquely their own — they created the <span className="text-white font-semibold">Japan World Football League</span>.
            JWFL is their tribute to the sport, the culture, and the community.
          </p>

          <div className="flex items-center gap-6 mt-6">
            <div className="flex items-center gap-2 text-[#7A86B5] text-xs">
              <Code size={14} style={{ color: '#00E5FF' }} />
              Built with passion
            </div>
            <div className="flex items-center gap-2 text-[#7A86B5] text-xs">
              <SoccerBall size={14} style={{ color: '#FF006E' }} />
              Powered by football
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center">
          <p className="text-[#3A4060] text-xs tracking-widest uppercase">
            JWFL &copy; {new Date().getFullYear()} · All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};
